"use client";

import React, { useCallback, useState, type CSSProperties } from "react";
import { Loader2 } from "lucide-react";

import useGeminiWs from "@/hooks/use-gemini-ws";
import { usePublicGeminiChatbotQuery } from "@/hooks/use-public-gemini-chatbot";

import FloatingButton from "./voice-agent/FloatingButton";
import GeminiChatPanel from "./gemini/GeminiChatPanel";

const DEFAULT_AVATAR = "https://api.dicebear.com/7.x/avataaars/svg?seed=Felix";
/** globals.css の --showcan-ink の既定と同じ */
const DEFAULT_BRAND = "#e99bad";

/** 先に書いたものから採る。空文字と空白だけの指定は「指定なし」として飛ばす */
const pick = (...values: (string | undefined)[]) =>
  values.find((value) => value?.trim());

/**
 * スクリプトタグで指定された表示内容。管理画面の設定より優先する。
 *
 * 管理画面を触れない・埋め込み側だけで出し分けたい、という運用のための逃げ道。
 */
export type EmbeddingGeminiChatOverrides = {
  agentName?: string;
  agentLabel?: string;
  avatarUrl?: string;
  iconUrl?: string;
  greeting?: string;
  inputPlaceholder?: string;
  idleStatus?: string;
  brandColor?: string;
};

export type EmbeddingGeminiChatProps = {
  agentId: string;
  overrides?: EmbeddingGeminiChatOverrides;
  /**
   * レイテンシ実測用。data-show-metrics="true" のときだけ計測値を画面下部に出す。
   * 訪問者に見せるものではないので既定はオフ
   */
  showMetrics?: boolean;
};

/**
 * 埋め込みウィジェット。
 *
 * 中身は公開ページ（/voice）と同じ画面を使う。同じボットなのに入口で
 * 操作が違うと、片方だけ直して差が開くため。
 * ここが持つのは右下のボタンとパネルの枠だけ。
 */
export default function EmbeddingGeminiChat({
  agentId,
  overrides = {},
  showMetrics = false,
}: EmbeddingGeminiChatProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { data: chatbot, isLoading, error } = usePublicGeminiChatbotQuery(agentId);
  const gemini = useGeminiWs(agentId);

  // 管理画面で「埋め込み先の指定を使う」をオフにした項目は、スクリプトタグの指定もCSS変数も見ない。
  // 埋め込みコードを直せないお客様でも、管理画面で変えた画像・色が出るようにするため
  const allow = chatbot?.embedOverrides ?? { avatar: true, launcherIcon: true, brandColor: true };

  // スクリプトタグの指定 → 管理画面の設定 → 既定、の順に採る
  const agentName = pick(overrides.agentName, chatbot?.name) ?? "AI Agent";
  const avatarUrl =
    pick(allow.avatar ? overrides.avatarUrl : undefined, chatbot?.avatarUrl) ?? DEFAULT_AVATAR;
  const iconUrl = pick(allow.launcherIcon ? overrides.iconUrl : undefined, chatbot?.iconUrl);
  const brandColor = pick(allow.brandColor ? overrides.brandColor : undefined, chatbot?.themeColor);

  // 管理画面のテーマカラーをウィジェット全体のブランドカラーにする。
  // 埋め込み先が --showcan-brand を指定していればそちらが勝つ（globals.css の段組み）。
  // 上書きを切ったときは --showcan-brand をここで書いて、埋め込み先から継承した値を塞ぐ
  const brandStyle = {
    display: "contents",
    ...(brandColor ? { "--showcan-agent-brand": brandColor } : {}),
    ...(!allow.brandColor ? { "--showcan-brand": brandColor ?? DEFAULT_BRAND } : {}),
  } as CSSProperties;

  const greeting = pick(overrides.greeting, chatbot?.greetingMessage);

  // マイクはオンで開く。ボタンを押して開くので、許可の要求もその操作の中で完結する
  // （公開ページはページを開いただけで接続するため、そちらはマイク無しで繋ぐ）
  const handleOpen = useCallback(async () => {
    setIsOpen(true);
    await gemini.connect();
  }, [gemini.connect]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    gemini.disconnect();
  }, [gemini.disconnect]);

  if (isLoading || error || !chatbot) return null;

  return (
    // showcan-root は --showcan-ink の定義が載る目印。付け忘れるとブランドカラーが既定に戻る
    <div className="showcan-root" style={brandStyle}>
      <FloatingButton
        agentName={agentName}
        agentLabel={overrides.agentLabel ?? "AI Agent"}
        avatarUrl={avatarUrl}
        iconUrl={iconUrl}
        avatarOverridable={allow.avatar}
        iconOverridable={allow.launcherIcon}
        isOpen={isOpen}
        onClick={handleOpen}
      />

      {isOpen && (
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-0 md:items-end md:justify-end md:p-6">
          <div
            className="showcan-overlay pointer-events-auto absolute inset-0 bg-black/20 backdrop-blur-[2px]"
            onClick={handleClose}
          />

          <div className="showcan-panel pointer-events-auto relative flex h-full w-full flex-col overflow-hidden bg-white shadow-2xl md:h-[650px] md:w-[400px] md:rounded-3xl">
            {gemini.status === "connecting" && (
              <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
                <Loader2 className="mb-3 h-8 w-8 animate-spin text-[var(--showcan-ink)]" />
                <span className="text-sm text-slate-500">接続中...</span>
              </div>
            )}

            <GeminiChatPanel
              gemini={gemini}
              agentName={agentName}
              avatarUrl={avatarUrl}
              avatarOverridable={allow.avatar}
              greeting={greeting}
              inputPlaceholder={overrides.inputPlaceholder}
              idleStatus={overrides.idleStatus}
              onClose={handleClose}
              showMetrics={showMetrics}
            />
          </div>
        </div>
      )}
    </div>
  );
}
